import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, locationsAPI } from '../../api/bookings';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { formatDate } from '../../lib/utils';
import DateTimeDisplay from '../../components/booking/DateTimeDisplay';
import { 
  Clock, Search, Phone, Mail, MapPin, Calendar, Filter, ArrowRight, ChevronDown, ChevronUp
} from 'lucide-react';

export default function WaitlistManagement() {
  const navigate = useNavigate();
  const [waitlist, setWaitlist] = useState([]);
  const [filteredWaitlist, setFilteredWaitlist] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConvertedGroups, setShowConvertedGroups] = useState({}); // groupKey -> bool
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    loadWaitlist();
    loadLocations();
  }, []);

  const loadLocations = async () => {
    try {
      const locs = await locationsAPI.getAllLocations();
      setLocations(locs);
    } catch (error) {
      console.error('Failed to load locations:', error);
    }
  };

  const loadWaitlist = async () => {
    setLoading(true);
    try {
      const data = await adminAPI.getWaitlist();
      setWaitlist(data);
    } catch (error) {
      toast.error('Failed to load waitlist');
    } finally {
      setLoading(false);
    }
  };

  const filterWaitlist = useCallback(() => {
    let filtered = [...waitlist];
    
    // Filter by search term (customer name or phone)
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(entry => 
        entry.customer_name?.toLowerCase().includes(search) ||
        entry.customer_phone?.toLowerCase().includes(search) ||
        entry.customer_email?.toLowerCase().includes(search)
      );
    }
    
    // Filter by location
    if (locationFilter !== 'all') {
      filtered = filtered.filter(entry => entry.location_id === locationFilter);
    }
    
    // Filter by date
    if (dateFilter) {
      filtered = filtered.filter(entry => entry.preferred_date === dateFilter);
    }
    
    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(entry => entry.status === statusFilter);
    }
    
    setFilteredWaitlist(filtered);
  }, [waitlist, searchTerm, locationFilter, dateFilter, statusFilter]);

  useEffect(() => {
    filterWaitlist();
  }, [filterWaitlist]);

  // Removed handleConfirmBooking - no longer needed with automatic promotion

  const handleCancelEntry = async (entryId, customerName) => {
    if (!window.confirm(`Cancel waitlist entry for ${customerName}?`)) {
      return;
    }
    
    try {
      await adminAPI.cancelWaitlistEntry(entryId, 'Cancelled by admin');
      toast.success('Waitlist entry cancelled');
      loadWaitlist();
    } catch (error) {
      toast.error('Failed to cancel entry');
    }
  };

  const getLocationName = (locationId) => {
    const location = locations.find(l => l.id === locationId);
    return location ? location.name : locationId;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'WAITING':
        return 'bg-yellow-100 text-yellow-700';
      case 'CONVERTED':
        return 'bg-green-100 text-green-700';
      case 'EXPIRED':
        return 'bg-gray-100 text-gray-700';
      case 'CANCELLED':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  // Group entries by location, date, and time slot
  const groupedEntries = filteredWaitlist.reduce((acc, entry) => {
    const key = `${entry.location_id}|${entry.preferred_date}|${entry.preferred_time_slot}`;
    if (!acc[key]) {
      acc[key] = {
        key,
        location_id: entry.location_id,
        date: entry.preferred_date,
        time_slot: entry.preferred_time_slot,
        entries: []
      };
    }
    acc[key].entries.push(entry);
    return acc;
  }, {});

  // Sort entries within each group by created_at (FIFO)
  Object.values(groupedEntries).forEach(group => {
    group.entries.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  });

  const goToBooking = (bookingId, bookingStatus) => {
    if (!bookingId) return;
    const shortRef = bookingId.slice(0, 8);

    // Route to the page that actually displays the booking based on its current status.
    // PENDING_ALLOCATION lives on Pending Requests. AWAITING_PAYMENT / CONFIRMED /
    // CANCELLED live on the Bookings page under different tabs.
    if (!bookingStatus || bookingStatus === 'PENDING_ALLOCATION') {
      navigate(`/admin/pending?ref=${shortRef}`);
      return;
    }
    if (bookingStatus === 'AWAITING_PAYMENT') {
      navigate(`/admin/bookings?ref=${shortRef}&status=AWAITING_PAYMENT`);
      return;
    }
    if (bookingStatus === 'CONFIRMED') {
      navigate(`/admin/bookings?ref=${shortRef}&status=CONFIRMED`);
      return;
    }
    // CANCELLED or unknown — fall back to Bookings page so admin can still search
    navigate(`/admin/bookings?ref=${shortRef}`);
  };

  const copyBookingRef = async (e, bookingId) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(bookingId.slice(0, 8).toUpperCase());
      toast.success('Booking ref copied');
    } catch (e) {
      // Clipboard can fail silently in some contexts; navigation still works on the parent click
      console.debug('clipboard write failed:', e?.message || e);
    }
  };

  // Status chip config mirrors the Bookings page colors so the admin sees the
  // same visual language across both pages. Keep this short - defensive fallback
  // is baked into getBookingStatusChip below.
  const BOOKING_STATUS_CHIP = {
    PENDING_ALLOCATION: { label: 'PENDING',    cls: 'bg-blue-100 text-blue-800' },
    AWAITING_PAYMENT:   { label: 'AWAITING',   cls: 'bg-amber-100 text-amber-800' },
    CONFIRMED:          { label: 'CONFIRMED',  cls: 'bg-emerald-200 text-emerald-900' },
    CANCELLED:          { label: 'CANCELLED',  cls: 'bg-gray-200 text-gray-700' },
    PAYMENT_FAILED:     { label: 'FAILED',     cls: 'bg-red-100 text-red-800' },
  };
  const getBookingStatusChip = (status) =>
    BOOKING_STATUS_CHIP[status] || { label: status || 'UNKNOWN', cls: 'bg-slate-100 text-slate-700' };

  const toggleConverted = (groupKey) => {
    setShowConvertedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  return (
    <div data-testid="waitlist-management-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-4xl font-black font-heading uppercase">Waitlist Management</h1>
        <div className="text-sm text-muted-foreground">
          {filteredWaitlist.length} {filteredWaitlist.length === 1 ? 'entry' : 'entries'}
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <Label className="text-xs uppercase font-bold text-muted-foreground">
            Filters
          </Label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs uppercase font-bold text-muted-foreground mb-2 block">
              <Search className="w-3 h-3 inline mr-1" />
              Search
            </Label>
            <Input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Name or phone..."
              className="h-10 rounded-xl"
              data-testid="waitlist-search"
            />
          </div>

          <div>
            <Label className="text-xs uppercase font-bold text-muted-foreground mb-2 block">
              <MapPin className="w-3 h-3 inline mr-1" />
              Location
            </Label>
            <Select value={locationFilter} onValueChange={setLocationFilter}>
              <SelectTrigger className="h-10 rounded-xl" data-testid="location-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {locations.map(loc => (
                  <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs uppercase font-bold text-muted-foreground mb-2 block">
              <Calendar className="w-3 h-3 inline mr-1" />
              Date
            </Label>
            <Input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="h-10 rounded-xl"
              data-testid="date-filter"
            />
          </div>

          <div>
            <Label className="text-xs uppercase font-bold text-muted-foreground mb-2 block">
              Status
            </Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-10 rounded-xl" data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="WAITING">Waiting</SelectItem>
                <SelectItem value="CONVERTED">Converted</SelectItem>
                <SelectItem value="CANCELLED">Cancelled</SelectItem>
                <SelectItem value="EXPIRED">Expired</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Loading waitlist...</div>
      ) : filteredWaitlist.length === 0 ? (
        <Card className="p-8 text-center">
          <Clock className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
          <p className="text-muted-foreground">
            {waitlist.length === 0 
              ? 'No waitlist entries yet' 
              : 'No entries match your filters'}
          </p>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.values(groupedEntries).map((group) => {
            const activeEntries = group.entries.filter(e => e.status !== 'CONVERTED');
            const convertedEntries = group.entries.filter(e => e.status === 'CONVERTED');
            const isConvertedShown = !!showConvertedGroups[group.key];
            // If group has ONLY converted entries, default to showing them so the group isn't an empty card
            const visibleEntries = activeEntries.length === 0
              ? convertedEntries
              : (isConvertedShown ? [...activeEntries, ...convertedEntries] : activeEntries);

            return (
            <Card key={group.key} className="px-4 py-3" data-testid={`waitlist-group-${group.key}`}>
              {/* Group Header — compact single row */}
              <div className="mb-2 pb-2 border-b flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <MapPin className="w-4 h-4 text-primary" />
                  <span className="font-bold text-base">{getLocationName(group.location_id)}</span>
                  <span className="text-gray-400">·</span>
                  <DateTimeDisplay
                    date={group.date}
                    timeSlot={group.time_slot}
                    duration={1}
                    testId={`admin-waitlist-${group.location_id}`}
                  />
                </div>
                <span className="text-xs font-bold uppercase bg-muted px-2 py-1 rounded-full">
                  {activeEntries.length} in queue
                </span>
              </div>

              {/* Queue Entries — compact 1-row cards */}
              <div className="space-y-2">
                {visibleEntries.map((entry) => {
                  const position = group.entries.indexOf(entry);
                  const isConverted = entry.status === 'CONVERTED';
                  return (
                  <div
                    key={entry.id}
                    className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg transition-colors flex-wrap ${
                      isConverted
                        ? 'bg-emerald-50/40 hover:bg-emerald-50/60 border border-emerald-100'
                        : 'bg-muted/30 hover:bg-muted/50'
                    }`}
                    data-testid="waitlist-entry"
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      {/* Queue Position */}
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <span className="font-black text-primary text-xs">#{position + 1}</span>
                      </div>

                      {/* Customer info — compact inline */}
                      <div className="min-w-0 flex-1 text-sm">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-gray-900">{entry.customer_name}</span>
                          <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${getStatusColor(entry.status)}`}>
                            {entry.status}
                          </span>
                          {entry.is_guest && (
                            <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold uppercase">
                              Guest
                            </span>
                          )}
                          <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                            <Phone className="w-3 h-3" /> {entry.customer_phone}
                          </span>
                          {entry.customer_email && (
                            <span className="inline-flex items-center gap-1 text-xs text-gray-600 truncate max-w-[220px]">
                              <Mail className="w-3 h-3" /> {entry.customer_email}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          Joined {formatDate(entry.created_at)} · {entry.sport} · {entry.courts_needed} court{entry.courts_needed !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      {isConverted && entry.booking_id ? (
                        (() => {
                          const chip = getBookingStatusChip(entry.booking_status);
                          return (
                            <button
                              type="button"
                              onClick={() => goToBooking(entry.booking_id, entry.booking_status)}
                              onContextMenu={(e) => copyBookingRef(e, entry.booking_id)}
                              data-testid={`waitlist-booking-ref-${entry.id}`}
                              title={
                                entry.booking_status
                                  ? `${entry.booking_status.replace(/_/g, ' ')} · Open in ${entry.booking_status === 'PENDING_ALLOCATION' ? 'Pending Requests' : 'Bookings'} · right-click to copy ref`
                                  : 'Open booking · right-click to copy ref'
                              }
                              className="group inline-flex items-center gap-1.5 pl-2.5 pr-2 py-1 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-800 text-xs font-semibold hover:bg-emerald-100 hover:border-emerald-400 transition-colors"
                            >
                              <span>#{entry.booking_id.slice(0, 8).toUpperCase()}</span>
                              <span className="text-emerald-400">·</span>
                              <span
                                data-testid={`waitlist-booking-status-${entry.id}`}
                                className={`px-1.5 py-0.5 rounded-full text-[10px] font-black tracking-wider ${chip.cls}`}
                              >
                                {chip.label}
                              </span>
                              <ArrowRight className="w-3 h-3 ml-0.5 text-emerald-700 group-hover:translate-x-0.5 transition-transform" />
                            </button>
                          );
                        })()
                      ) : entry.status === 'WAITING' ? (
                        <Button
                          onClick={() => handleCancelEntry(entry.id, entry.customer_name)}
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50"
                          data-testid={`waitlist-cancel-${entry.id}`}
                        >
                          Cancel
                        </Button>
                      ) : null}
                    </div>
                  </div>
                  );
                })}
              </div>

              {/* (E) Collapse converted entries toggle */}
              {activeEntries.length > 0 && convertedEntries.length > 0 && (
                <div className="mt-3 pt-3 border-t">
                  <button
                    type="button"
                    onClick={() => toggleConverted(group.key)}
                    data-testid={`toggle-converted-${group.key}`}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {isConvertedShown ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    {isConvertedShown ? 'Hide' : 'Show'} converted ({convertedEntries.length})
                  </button>
                </div>
              )}
            </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
